import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import axios from 'axios';
import { FileXIcon, FilmIcon, TrashIcon, UploadIcon } from 'lucide-react';
import {
  CREATIVE_FIELD_PREFIX,
  fieldDef,
  isVideoFile,
  isNodePublished,
  jobAssetFailure,
  nodeFieldValue,
  nodeName,
  useJobAsset,
  useSetNodeImage,
  useSetNodeLogo,
  useSetNodeVideo,
  type JobNode,
} from '@/entities/media-campaign';
import { notificationService } from '@/lib/services/notification.service';
import { useTranslation } from 'react-i18next';

/** What a platform will take as a creative, mirroring the server's own list. */
const ACCEPTED = '.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.webm';

/** A logo is a still, so the picker offers only the half of that list a logo can be. */
const ACCEPTED_LOGO = '.jpg,.jpeg,.png,.gif,.webp';

/**
 * The server's own ceilings, checked here as well because the server cannot check them
 * cheaply: it only learns the size after streaming and hashing the whole object back out of
 * storage, so an oversize file is uploaded in full, egressed in full, and only then refused.
 * A video master makes that a minutes-long wait for a no.
 *
 * Video is allowed far more than an image because a social master is that much bigger; both
 * must stay in step with `asset-key.ts`, or this refuses files the server would take.
 */
const MAX_IMAGE_BYTES = 30 * 1024 * 1024;
const MAX_VIDEO_BYTES = 1024 * 1024 * 1024;

/** Gigabytes past 1024 MB, because "1024 MB" reads as a number nobody chose. */
function describeLimit(bytes: number): string {
  const megabytes = bytes / 1024 / 1024;
  return megabytes >= 1024 ? `${megabytes / 1024} GB` : `${megabytes} MB`;
}

export interface CreativePreviewDialogProps {
  /** Needed to fetch and to replace the asset the plan was uploaded with. */
  jobId: string;
  /** Which columns a creative has here, and so whether a logo is one of them. */
  platform: string;
  node: JobNode | null;
  onClose: () => void;
}

/**
 * Why there is nothing to show, in the words that say what to do about it.
 *
 * The cases are genuinely different: a row that names no asset at all, one naming an asset
 * the upload did not carry, one whose fetch failed for any other reason, a plan sent as a
 * bare sheet, and a video whose bytes arrived but that this browser cannot decode. A single
 * "no preview" message for all of them would hide which one applies, and telling a fetch
 * fault it was never uploaded costs someone a re-upload of files already there.
 */
function EmptyPreview({
  named,
  failure,
  undecodable = false,
}: {
  named: string | null;
  failure: ReturnType<typeof jobAssetFailure>;
  undecodable?: boolean;
}) {
  const { t } = useTranslation('projects');
  const message = undecodable
    ? t('deliveryJobReview.creativePreviewDialog.videoCannotBePlayed', { named: named ?? '' })
    : !named
      ? t('deliveryJobReview.creativePreviewDialog.noAssetYet')
      : failure === 'missing'
        ? t('deliveryJobReview.creativePreviewDialog.notAmongUploadedFiles', { named })
        : failure === 'unavailable'
          ? t('deliveryJobReview.creativePreviewDialog.couldNotBeLoaded', { named })
          : t('deliveryJobReview.creativePreviewDialog.loading');

  return (
    <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
      <FileXIcon className="size-8" aria-hidden />
      <span className="max-w-[28rem] text-balance text-center text-sm">{message}</span>
    </div>
  );
}

const YOUTUBE_VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'www.youtube-nocookie.com',
]);
/** Paths that carry the id as their second segment, e.g. `/shorts/<id>`. */
const YOUTUBE_ID_PATH = /^(shorts|embed|live|v)$/;

/**
 * The id a YouTube link or a bare id names, or null. Mirrors media-api's `youtubeVideoId`,
 * so the video opened from here is the one the publish would play.
 */
function youtubeVideoId(raw: string): string | null {
  const value = raw.trim();
  if (YOUTUBE_VIDEO_ID.test(value)) return value;

  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase();
  const [first, second] = url.pathname.split('/').filter(Boolean);

  let id: string | null | undefined;
  if (host === 'youtu.be') id = first;
  else if (YOUTUBE_HOSTS.has(host) && first === 'watch') id = url.searchParams.get('v');
  else if (YOUTUBE_HOSTS.has(host) && YOUTUBE_ID_PATH.test(first)) id = second;

  return id && YOUTUBE_VIDEO_ID.test(id) ? id : null;
}

/**
 * A video named by its YouTube link has no file in the plan, so the link is what there is to
 * check. The href is rebuilt from the parsed id rather than taken from the cell, which a
 * spreadsheet could fill with a `javascript:` URL.
 */
function LinkedVideoPreview({ link }: { link: string }) {
  const { t } = useTranslation('projects');
  const videoId = youtubeVideoId(link);
  const linkClass = 'max-w-[28rem] break-all text-center text-sm';

  return (
    <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
      <FilmIcon className="size-8" aria-hidden />
      <span className="max-w-[28rem] text-balance text-center text-sm">
        {t('deliveryJobReview.creativePreviewDialog.playsYouTubeVideo')}
      </span>
      {videoId ? (
        <a
          href={`https://www.youtube.com/watch?v=${videoId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`${linkClass} text-primary hover:underline`}
        >
          {link}
        </a>
      ) : (
        <span className={`${linkClass} text-foreground`}>{link}</span>
      )}
    </div>
  );
}

/**
 * The thumbnail, standing in for a video this browser will not decode.
 *
 * The named thumbnail is a still of the same creative, so it answers the question the
 * dialog is open for — whether the right asset is on this ad — where a message on its own
 * leaves it unanswered. The message stays underneath, because what is on screen is not
 * the video and nothing should suggest it played.
 */
function UnplayableVideo({ poster, named }: { poster: string; named: string | null }) {
  const { t } = useTranslation('projects');

  return (
    <figure className="flex flex-col items-center gap-3">
      <img
        src={poster}
        alt={named ?? ''}
        className="max-h-[52vh] w-auto rounded-md object-contain"
        data-testid="creative-preview-poster"
      />
      <figcaption className="max-w-[28rem] text-balance text-center text-sm text-muted-foreground">
        {t('deliveryJobReview.creativePreviewDialog.videoCannotBePlayed', { named: named ?? '' })}
      </figcaption>
    </figure>
  );
}

/**
 * The second file a video ad needs, under the one everyone came to look at.
 *
 * Google draws a logo beside the video and refuses an ad without one, so the pre-flight
 * finding that says so has to be answerable here. Small and secondary because it is: the
 * video is the creative, and the logo is a brand mark reused across every ad.
 */
function LogoSlot({
  src,
  named,
  canEdit,
  busy,
  onPick,
  onRemove,
}: {
  src: string | undefined;
  named: string | null;
  canEdit: boolean;
  busy: boolean;
  onPick: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('projects');
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-border p-3"
      data-testid="creative-logo-slot"
    >
      {src ? (
        <img
          src={src}
          alt={named ?? ''}
          className="size-12 shrink-0 rounded-md object-contain"
          data-testid="creative-logo-image"
        />
      ) : (
        <div className="flex size-12 shrink-0 items-center justify-center rounded-md bg-surface dark:bg-surface-raised">
          <FileXIcon className="size-5 text-muted-foreground" aria-hidden />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{t('deliveryJobReview.creativePreviewDialog.logo')}</p>
        <p className="truncate text-sm text-muted-foreground">
          {named ?? t('deliveryJobReview.creativePreviewDialog.noLogoYet')}
        </p>
      </div>
      {canEdit && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_LOGO}
            onChange={onPick}
            className="hidden"
            data-testid="creative-logo-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid="creative-logo-upload"
          >
            <UploadIcon className="size-4" />
            {named
              ? t('deliveryJobReview.creativePreviewDialog.replaceLogo')
              : t('deliveryJobReview.creativePreviewDialog.addALogo')}
          </Button>
          {named && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemove}
              disabled={busy}
              data-testid="creative-logo-remove"
            >
              <TrashIcon className="size-4" />
              {t('deliveryJobReview.creativePreviewDialog.remove')}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The creative at full size, and where its asset is managed.
 *
 * The sheet can only afford a 36px thumbnail, which is enough to tell two ads apart
 * and nowhere near enough to check one — so the column opens this. Attaching and
 * replacing live here too rather than in a separate surface: the moment you want to
 * change an asset is the moment you have just looked at it.
 */
export function CreativePreviewDialog({
  jobId,
  platform,
  node,
  onClose,
}: CreativePreviewDialogProps) {
  const { t } = useTranslation('projects');
  // The creative's fields live on the ad row behind `CREATIVE_FIELD_PREFIX`; a bare
  // `creative` key matched nothing, so this always claimed the file named none.
  const creative = node ? nodeFieldValue(node, `${CREATIVE_FIELD_PREFIX}name`) : null;
  const imageFile = node ? nodeFieldValue(node, `${CREATIVE_FIELD_PREFIX}imageFile`) : null;
  const videoFile = node ? nodeFieldValue(node, `${CREATIVE_FIELD_PREFIX}videoFile`) : null;
  const thumbnailFile = node ? nodeFieldValue(node, `${CREATIVE_FIELD_PREFIX}thumbnailFile`) : null;
  const logoFile = node ? nodeFieldValue(node, `${CREATIVE_FIELD_PREFIX}logoFile`) : null;
  const youtubeVideoUrl = node
    ? nodeFieldValue(node, `${CREATIVE_FIELD_PREFIX}youtubeVideoUrl`)
    : null;
  // A video already on YouTube makes this a video ad too, though there is no file here to play.
  const servesVideo = Boolean(videoFile ?? youtubeVideoUrl);
  // One row serves one asset, so whichever column is filled is the asset to show.
  const assetFile = videoFile ?? imageFile;
  const { data: src, error } = useJobAsset(jobId, assetFile, videoFile ? 'video' : 'image');
  // A poster for the video tag, so there is a frame on screen while the file streams in
  // rather than a blank rectangle. Only fetched for a video row with a thumbnail named.
  const { data: posterSrc } = useJobAsset(jobId, videoFile ? thumbnailFile : null, 'image');
  // The logo is drawn beside the video, so it belongs to a video ad and to nothing else.
  // Whether the platform has the column at all is the schema's answer, which is also what
  // keeps this off a platform that never gained one.
  const showsLogo =
    servesVideo && fieldDef(platform, 'ad', `${CREATIVE_FIELD_PREFIX}logoFile`) !== undefined;
  const { data: logoSrc } = useJobAsset(jobId, showsLogo ? logoFile : null, 'image');
  const setImage = useSetNodeImage(jobId);
  const setVideo = useSetNodeVideo(jobId);
  const setLogo = useSetNodeLogo(jobId);
  const busy = setImage.isPending || setVideo.isPending || setLogo.isPending;
  const inputRef = useRef<HTMLInputElement>(null);
  // A video that fails to decode fires `error` rather than ever reaching `onSuccess`, so
  // that is the only signal a black rectangle is all this browser will ever show for it.
  const [videoUnplayable, setVideoUnplayable] = useState(false);
  useEffect(() => setVideoUnplayable(false), [src]);

  // The asset belongs to the creative; the ad only references one. A row with no
  // creative has nothing to attach a file to.
  const creativeNodeId = node?.creativeNodeId ?? null;
  // Changing the local plan cannot change an ad already serving, so the row is locked
  // for the same reason every other field on it is.
  const locked = node !== null && isNodePublished(node);
  const canEdit = creativeNodeId !== null && !locked;

  const send = (setAsset: typeof setImage, file: File | null) => {
    if (!creativeNodeId) return;

    setAsset.mutate(
      { nodeId: creativeNodeId, file },
      {
        onSuccess: () =>
          notificationService.showSuccess(
            file
              ? t('deliveryJobReview.creativePreviewDialog.fileAttached')
              : t('deliveryJobReview.creativePreviewDialog.fileRemoved'),
          ),
        onError: (error) => {
          // A response reaching here already triggered the global problem-details toast;
          // showing axios's own generic message too ("Request failed with status code
          // 400") would put a second, useless one beside the message that actually says
          // what went wrong.
          if (axios.isAxiosError(error) && error.response) return;
          notificationService.showError(
            error instanceof Error
              ? error.message
              : t('deliveryJobReview.creativePreviewDialog.couldNotChangeTheFile'),
          );
        },
      },
    );
  };

  const apply = (file: File | null) => {
    // Attaching routes on the kind of file just picked, not on what the row already holds:
    // a row naming no video still has to be able to receive its first one. Removing has no
    // file to read, so it goes to whichever route owns the asset on show.
    send((file ? isVideoFile(file) : Boolean(videoFile)) ? setVideo : setImage, file);
  };

  /** Answers false having already said why, so a caller only has to stop. */
  const withinLimit = (file: File, limit: number): boolean => {
    if (file.size <= limit) return true;
    notificationService.showError(
      t('deliveryJobReview.creativePreviewDialog.fileTooLarge', {
        named: file.name,
        size: Math.ceil(file.size / 1024 / 1024),
        limit: describeLimit(limit),
      }),
    );
    return false;
  };

  /**
   * The file just picked, or nothing.
   *
   * The input is cleared either way so choosing the same file twice still fires a change,
   * which is exactly what happens when someone re-exports over the top and picks it again.
   */
  const picked = (event: ChangeEvent<HTMLInputElement>): File | undefined => {
    const file = event.target.files?.[0];
    event.target.value = '';
    return file;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = picked(event);
    if (!file) return;
    if (!withinLimit(file, isVideoFile(file) ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES)) return;
    apply(file);
  };

  const handleLogoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = picked(event);
    if (!file) return;
    // A logo is a still whatever the picker was handed, so the picture ceiling is the one
    // that applies even to a file that arrived past the `accept` filter.
    if (!withinLimit(file, MAX_IMAGE_BYTES)) return;
    send(setLogo, file);
  };

  return (
    <Dialog open={node !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="w-[min(calc(100vw-2rem),52rem)]"
        data-testid="creative-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle>{node ? nodeName(node) : ''}</DialogTitle>
          <DialogDescription>
            {[creative, assetFile ?? youtubeVideoUrl]
              .filter(Boolean)
              .join(t('deliveryJobReview.creativePreviewDialog.detailJoin')) ||
              t('deliveryJobReview.creativePreviewDialog.noCreativeNamedInTheFile')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[65vh] items-center justify-center overflow-auto rounded-lg bg-surface p-4 dark:bg-surface-raised">
          {src ? (
            videoFile ? (
              videoUnplayable ? (
                posterSrc ? (
                  <UnplayableVideo poster={posterSrc} named={assetFile} />
                ) : (
                  <EmptyPreview named={assetFile} failure={null} undecodable />
                )
              ) : (
                // eslint-disable-next-line jsx-a11y/media-has-caption -- no caption files travel with a plan creative, so there is nothing to point a track at
                <video
                  src={src}
                  controls
                  poster={posterSrc}
                  preload="metadata"
                  aria-label={assetFile ?? ''}
                  onError={() => setVideoUnplayable(true)}
                  className="max-h-[60vh] w-auto rounded-md"
                  data-testid="creative-preview-video"
                />
              )
            ) : (
              // Contained rather than cropped: this is the check that the right asset
              // is on the right ad, and a cover crop can hide the edges of a 9:16.
              <img
                src={src}
                alt={assetFile ?? ''}
                className="max-h-[60vh] w-auto rounded-md object-contain"
                data-testid="creative-preview-image"
              />
            )
          ) : youtubeVideoUrl && !assetFile ? (
            <LinkedVideoPreview link={youtubeVideoUrl} />
          ) : (
            <EmptyPreview named={assetFile} failure={jobAssetFailure(error)} />
          )}
        </div>

        {showsLogo && (
          <LogoSlot
            src={logoSrc}
            named={logoFile}
            canEdit={canEdit}
            busy={busy}
            onPick={handleLogoChange}
            onRemove={() => send(setLogo, null)}
          />
        )}

        <DialogFooter>
          {canEdit && (
            <>
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED}
                onChange={handleChange}
                className="hidden"
                data-testid="creative-image-input"
              />
              <Button
                variant="outline"
                size="default"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                className="mr-auto"
                data-testid="creative-image-upload"
              >
                <UploadIcon className="size-4" />
                {busy
                  ? t('deliveryJobReview.creativePreviewDialog.uploading')
                  : assetFile || youtubeVideoUrl
                    ? t('deliveryJobReview.creativePreviewDialog.replaceFile')
                    : t('deliveryJobReview.creativePreviewDialog.addAFile')}
              </Button>
              {assetFile && (
                <Button
                  variant="ghost"
                  size="default"
                  onClick={() => apply(null)}
                  disabled={busy}
                  data-testid="creative-image-remove"
                >
                  <TrashIcon className="size-4" />
                  {t('deliveryJobReview.creativePreviewDialog.remove')}
                </Button>
              )}
            </>
          )}
          <Button variant="outline" size="default" onClick={onClose}>
            {t('deliveryJobReview.creativePreviewDialog.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
